import type { ExpandedSchedule, Timing } from "@patient-watch/types";

type TimingLike = Pick<Timing["Row"], "interval" | "time_unit" | "repeats" | "is_infinite" | "is_autoname" | "label">;

/** Stable fingerprint of a schedule's timing rules (ignores timing/schedule ids and names). */
export function scheduleTimingsFingerprint(timings: TimingLike[] | null | undefined): string {
    if (!timings?.length) return "";

    const normalised = timings
        .map((timing) => ({
            interval: Number(timing.interval ?? 0),
            time_unit: String(timing.time_unit ?? ""),
            repeats: Number(timing.repeats ?? 0),
            is_infinite: Boolean(timing.is_infinite),
            is_autoname: Boolean(timing.is_autoname),
            label: timing.is_autoname
                ? ""
                : String(timing.label ?? "")
                      .trim()
                      .toLowerCase(),
        }))
        .sort((a, b) => {
            if (a.interval !== b.interval) return a.interval - b.interval;
            if (a.time_unit !== b.time_unit) return a.time_unit.localeCompare(b.time_unit);
            if (a.repeats !== b.repeats) return a.repeats - b.repeats;
            if (a.is_infinite !== b.is_infinite) return Number(a.is_infinite) - Number(b.is_infinite);
            if (a.is_autoname !== b.is_autoname) return Number(a.is_autoname) - Number(b.is_autoname);
            return a.label.localeCompare(b.label);
        });

    return JSON.stringify(normalised);
}

/**
 * Keep one schedule per identical timings set.
 * Prefer `preferScheduleId` when it matches a duplicate group; otherwise keep the first seen.
 * Schedules with no timings stay unique by schedule_id.
 */
export function dedupeSchedulesByTimings(
    options: ExpandedSchedule[],
    preferScheduleId?: string | null
): ExpandedSchedule[] {
    const byKey = new Map<string, ExpandedSchedule>();

    for (const option of options) {
        const fingerprint = scheduleTimingsFingerprint(option.timings);
        const key = fingerprint || `id:${option.schedule_id}`;
        const existing = byKey.get(key);

        if (!existing) {
            byKey.set(key, option);
            continue;
        }

        if (preferScheduleId && option.schedule_id === preferScheduleId) {
            byKey.set(key, option);
        }
    }

    return [...byKey.values()];
}
